<script lang="ts" setup>
import { useMateTrait } from "@/stores/mateTrait";
import { useMate } from "@/stores/mate";
import { onBeforeMount, ref } from "vue";
import { onCurrentMateChange } from "@/stores/internal-subscribe";
import { useRouter } from "vue-router";
import CheckCircleFillIcon from "@/components/icon/CheckCircleFillIcon.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { showConfirmDialog } from "vant";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { watchThrottled } from "@vueuse/core";
import { useRoute } from "vue-router";
// Props
const route = useRoute();
const mateStore = useMate();
const props = withDefaults(
  defineProps<{
    reqData?: PromptJson;
    selectedTraitId?: number | null;
    selectedTrait?: MateTrait | null;
    enableSelection?: boolean; // 启用选择模式
    enableDelete?: boolean;
    enableEdit?: boolean;
    enableSelectionLock?: boolean; // 选择模式下，启用锁定selectedTraitId(不允许切换特征)
    enableSelectionOptional?: boolean; // 选择模式下，是否可以不选特征
    hidePresetStyle?: boolean; // 隐藏预设风格
    mateId?: number;
  }>(),
  {
    enableSelection: false,
    enableDelete: false,
    enableLock: false,
    enableSelectionLock: false,
    enableEdit: false,
    enableSelectionOptional: true,
    hidePresetStyle: false,
    mateId: 0,
  }
);
const { t } = useI18n();

const traitId = computed(
  () => props.selectedTraitId ?? props.selectedTrait?.id ?? null
);

const emit = defineEmits<{
  (e: "update:selectedTrait", trait: MateTrait | null): void;
  (e: "update:selectedTraitId", traitId: number | null): void;
  (e: "update:showCreateTraitModal", show: boolean): void;
}>();

// State
const router = useRouter();
const mateTrait = useMateTrait();
const loading = ref(false);

// Lifecycle
const prepareData = async () => {
  loading.value = true;
  try {
    await mateTrait.getOrFetchTraitListByMateId(
      props.mateId || mateStore.currentMate!.id!
    );
    if (!props.enableSelectionOptional && !traitId.value) {
      emit("update:selectedTrait", mateTrait.traitList[0]);
      emit("update:selectedTraitId", mateTrait.traitList[0].id!);
    }
    showTraitList.value = mateTrait.mateIdTraitListMap[props.mateId];
  } finally {
    loading.value = false;
  }
};

const showCreateTraitModal = ref(false);

onBeforeMount(() => {
  prepareData();
});

onCurrentMateChange(() => {
  prepareData();
});

// EventHandler
const onTraitSelect = (trait: MateTrait) => {
  if (!props.enableSelection || props.enableSelectionLock) return;

  // 如果已经选中了，再次点击则取消选中；若启用了锁定，则不取消选中
  if (props.enableSelectionOptional && traitId.value === trait.id) {
    emit("update:selectedTrait", null);
    emit("update:selectedTraitId", null);
  } else {
    emit("update:selectedTrait", trait);
    emit("update:selectedTraitId", trait.id!);
  }
};
const clickEditTrait = async (mateTrait: MateTrait) => {
  try {
    await showConfirmDialog({
      title: t("Message.editTrait"),
      message: t("Message.editTraitConfirm"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
    const { id, mode } = mateTrait;
    router.push({ name: "createTraitTask", query: { id, mode } });
  } catch (e) {}
};

const search = ref("");
const showTraitList = ref<MateTrait[]>([]);
watchThrottled(
  search,
  async (value) => {
    showTraitList.value = mateTrait.mateIdTraitListMap[props.mateId].filter(
      (trait) => trait.name.includes(value) || trait.prompt.includes(value)
    );
  },
  {
    throttle: 500,
  }
);
</script>
<template>
  <div class="flex text-base-content/50 text-xs py-2 h-full overflow-hidden">
    <div v-if="loading" class="w-full h-20 flex items-center justify-center">
      <span class="loading"></span>
    </div>
    <div
      v-else-if="mateTrait.mateIdTraitListMap[mateId].length === 0"
      class="text-center w-full"
    >
      <div>{{ $t(`painting.people.noFeatureInfo`) }}</div>
      <div>{{ $t(`painting.people.noFeatureInfoDesc`) }}</div>
      <button
        class="btn btn-xs rounded mt-2"
        @click="showCreateTraitModal = true"
      >
        {{ $t("painting.people.create") }}
      </button>
    </div>
    <div v-else class="w-full mx-3 overflow-hidden h-full">
      <VanSearch
        v-model="search"
        :placeholder="$t(`trait.traitName`)"
        class="bg-primary py-0 px-0 mb-1"
      />
      <div class="flex flex-col gap-2 overflow-auto h-full pb-12">
        <div
          v-for="item in showTraitList"
          v-show="!enableSelectionLock || traitId === item.id"
          :key="item.id"
          class="bg-primary px-3 py-2"
          @click="onTraitSelect(item)"
        >
          <div class="flex w-full gap-4">
            <div class="flex flex-col relative mb-3">
              <user-avatar
                :avatar-url="item.media?.url || item.source?.url"
                class="w-[84px] aspect-[3/4]"
              />
            </div>
            <div class="flex-1 flex flex-col">
              <h5>{{ item.name }}</h5>
              <p class="line-clamp-6 h-[90px] overflow-hidden">
                {{ item.prompt }}
              </p>
              <div class="text-right"></div>
            </div>
          </div>
          <div class="flex w-full justify-between">
            <div class="w-[84px] flex justify-center items-center">
              <div
                v-if="traitId === item.id"
                class="text-center shrink-0 flex items-center justify-center mb-1"
              >
                <check-circle-fill-icon class="text-primary icon-base mr-1" />
                <span class="line-clamp-1">
                  {{ $t(`painting.selected`) }}
                </span>
              </div>
              <div v-else class="text-center">
                <button class="btn btn-xs">
                  {{ t(`Buttons.select`) }}
                </button>
              </div>
            </div>
            <div class="flex-1 flex justify-between">
              <div v-if="traitId !== item.id || route.query.mode !== '6'"></div>
              <div v-else class="flex items-center">
                {{ t(`painting.people.position`) }}：
                <van-radio-group
                  v-model="reqData!.featureType"
                  direction="horizontal"
                  shape="dot"
                  @click.stop
                  icon-size="14px"
                >
                  <van-radio :name="1">
                    {{ t(`painting.people.position1`) }}
                  </van-radio>
                  <van-radio :name="2">
                    {{ t(`painting.people.position2`) }}
                  </van-radio>
                </van-radio-group>
              </div>
              <button
                v-if="enableEdit"
                class="btn btn-neutral btn-xs"
                @click.stop="clickEditTrait(item)"
              >
                <i class="iconify mingcute--edit-3-line text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CreateTraitPresetModal v-model:show="showCreateTraitModal" />
  </div>
</template>

<style scoped>
:deep(.van-search__content) {
  background-color: var(--color-primary);
}
</style>
